import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { LoadingOverlay, RECEIVABLES_API } from "./common";
import SheetPrintLayouts from "./SheetPrintLayouts";

const APPROVAL_FLAGS = [
  ["approveAllCollections", "Every collection needs approval"],
  ["approvalForCash", "Cash"],
  ["approvalForUpi", "UPI"],
  ["approvalForCheque", "Cheque"],
  ["approvalForEfund", "E-fund transfer"],
  ["approvalForCreditNote", "Credit note"],
  ["approvalForDebitNote", "Debit note"],
  ["approvalForDiscount", "Discount"],
  ["approvalForExpired", "Expired"],
];

const WORKFLOW_FLAGS = [
  ["loginOtpEnabled", "Login needs an OTP sent to the user's phone (admin panel and mobile app)"],
  ["colorCodingEnabled", "Colour-code outstanding invoices"],
  ["chequeClearOtpRequired", "Clearing cheques needs an OTP"],
  ["bounceAndReverseEnabled", "Allow bounce & reverse of approved cheques"],
  ["reverseBalanceOnBounce", "Bouncing a cheque takes it back off the invoice (off = legacy: balance stays reduced)"],
  ["loadSheetNumberRequired", "Load sheet number required (mobile app opens the load sheet collection screen)"],
  ["deliveryOtpSmsEnabled", "SMS delivery OTP to customers"],
  ["showDeliveryOtpToAdmin", "Show delivery OTPs to admins"],
  ["denominationReportEnabled", "Show cash denomination report"],
  ["creditNoteReferenceRequired", "Credit note takes a reference number on the Payments page (never asked for in the mobile app)"],
  ["manualPaymentUsesLoginUser", "Attribute manual payments to the logged-in user"],
  ["tallySettingsEnabled", "Show the Tally Settings button on the Collections (PDC) page"],
  ["lockInvoiceAfterLoadingSheet", "Lock sales invoice items once the loading sheet is printed"],
];

// isInvoiceCustomerWise: which hierarchy decides the salesman who sees an invoice in the mobile app.
const INVOICE_ASSIGNMENT = [
  {
    value: true,
    label: "Customer-wise",
    help: "Salesman → beats → customers. An invoice goes to its customer's beat; assign customers to beats on the Beats page.",
  },
  {
    value: false,
    label: "Beat-wise",
    help: "Salesman → beats. Each invoice is assigned to a beat directly when it is created, or later from Sales Invoices.",
  },
];

// locationEveryMinute: when the mobile app sends a field user's location.
const LOCATION_CAPTURE = [
  {
    value: false,
    label: "Every action",
    help: "The location is picked only when the user does an action, such as entering a payment. Nothing is picked in between.",
  },
  {
    value: true,
    label: "Every one minute",
    help: "The location is picked every minute while the app is open, and not on actions. An entry is saved even when the phone's location is off, with empty coordinates.",
  },
];

// appInvoiceForAllUsers: who gets Create Invoice in the mobile app.
const APP_INVOICE_USERS = [
  {
    value: false,
    label: "Tenant only",
    help: "Only the tenant (super admin) login can create invoices in the mobile app.",
  },
  {
    value: true,
    label: "All users",
    help: "Admins, sales managers, salesmen and delivery users can create invoices in the mobile app too.",
  },
];

const MOBILE_SETTINGS_API = "/api/v1/mobile/settings";

// Distributor-wide switches of the mobile app (mobile settings). The per-user ones are on the Users page.
const MOBILE_OTP_FLAGS = [
  ["customerOtpEnabled", "Payments need the customer's OTP (only for users with Payment OTP on)"],
  ["otpOnFullPayment", "Salesman: ask for the OTP on full payments too (off = part payments only)"],
  ["deliveryOtpOnFullPayment", "Delivery: ask for the OTP on full payments too (off = part payments only)"],
];

const MOBILE_FLAGS = [
  ["oldestInvoiceFirst", "Oldest invoice must be cleared first (only for users with Clear oldest invoice first on)"],
  ["upiBankRequired", "UPI payments need the bank name"],
  ["phoneUpdateOtpRequired", "Changing a customer's phone number needs an OTP"],
  ["locationRequired", "A payment cannot be saved without the phone's location"],
];

const MOBILE_AMOUNTS = [
  ["otpMinBalance", "Salesman: OTP only when a bill's balance is above (₹)"],
  ["deliveryOtpMinBalance", "Delivery: OTP only when a bill's balance is above (₹)"],
  ["oldestInvoiceMinBalance", "Oldest invoice counts as cleared when its balance is at most (₹)"],
];

// The remaining configurations of the TurboPay super-admin Configurations page (settings.tenantConfig), with TurboPay's own
// labels.
const TENANT_CONFIG_FLAGS = [
  ["includeCollectionEntries", "Include Collection Entries"],
  ["includeNoActivityReport", "Include No Activity Report"],
  ["minBalanceForOldestInvoiceNeeded", "Min Balance For Oldest Invoice Needed"],
  ["dayBeatAssigned", "Is Day Beat Assigned"],
  ["importExcelEnabled", "Is Import Excel Needed"],
  ["zeroBalanceSms", "Is Zero Balance Sms Needed"],
  ["cashApprovalOtpToAlternateNumber", "OTP Needed For Cash Approval By Alternate Number"],
  ["alwaysShowClosingBalance", "Always Show Closing Balance"],
];

const TENANT_CONFIG_NUMBERS = [
  ["denominationDifferenceAllowed", "Allowed Denomination Difference", "0.01"],
  ["maxChequeBouncesAllowed", "Maximum Bounce Cheques Allowed", "1"],
  ["maxOutstandingInvoicesAllowed", "Maximum Outstanding Invoices Allowed", "1"],
  ["lowStockThreshold", "Low Stock Alert Threshold (quantity left after open invoices)", "0.001"],
];

const TENANT_CONFIG_TEXTS = [
  ["chequeReportAddress", "Tenant Address For Cheque Report"],
];

const TEXT_FIELDS = [
  ["approverPhone", "Approver phone (receives approval OTPs)"],
  ["alternateApproverPhone", "Alternate approver phone"],
  ["accountHolderName", "Pay-in slip: account holder"],
  ["accountNumber", "Pay-in slip: account number"],
  ["panNumber", "Pay-in slip: PAN"],
];

const Checkbox = ({ field, label, form, onChange }) => (
  <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0" }}>
    <input type="checkbox" checked={Boolean(form[field])} onChange={(e) => onChange(field, e.target.checked)} />
    {label}
  </label>
);

/** Per-distributor switches for the collection workflow (legacy TenantConfig flags). */
const ReceivableSettings = () => {
  const [form, setForm] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`${RECEIVABLES_API}/settings`)
      .then(setForm)
      .catch((error) => toast.error(error.message || "Failed to load settings"));
    api
      .get(MOBILE_SETTINGS_API)
      .then(setMobile)
      .catch((error) => toast.error(error.message || "Failed to load mobile app settings"));
  }, []);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setMobileField = (field, value) => setMobile((prev) => ({ ...prev, [field]: value }));
  const setTenantConfig = (field, value) => setForm((prev) => ({ ...prev, tenantConfig: { ...(prev.tenantConfig || {}), [field]: value } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        chequeBounceCharges: form.chequeBounceCharges === "" || form.chequeBounceCharges === null ? 0 : Number(form.chequeBounceCharges),
        maxDiscountPercent: form.maxDiscountPercent === "" || form.maxDiscountPercent === null ? null : Number(form.maxDiscountPercent),
        tenantConfig: {
          ...(form.tenantConfig || {}),
          ...Object.fromEntries(TENANT_CONFIG_FLAGS.map(([field]) => [field, !!form.tenantConfig?.[field]])),
          ...Object.fromEntries(
            TENANT_CONFIG_NUMBERS.map(([field]) => {
              const value = form.tenantConfig?.[field];
              return [field, value === "" || value === null || value === undefined ? 0 : Number(value)];
            })
          ),
        },
      };
      setForm(await api.put(`${RECEIVABLES_API}/settings`, payload));
      if (mobile) {
        const amounts = Object.fromEntries(
          MOBILE_AMOUNTS.map(([field]) => [field, mobile[field] === "" || mobile[field] === null ? 0 : Number(mobile[field])])
        );
        setMobile(await api.put(MOBILE_SETTINGS_API, { ...mobile, ...amounts }));
      }
      toast.success("Settings saved");
    } catch (error) {
      toast.error(error.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <LoadingOverlay show label="Loading settings..." />;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={saving} label="Saving..." />
      <form onSubmit={save} className="page-panel" style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600 }}>How invoices reach salesmen</h3>
          {INVOICE_ASSIGNMENT.map((option) => (
            <label key={String(option.value)} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0" }}>
              <input
                type="radio"
                name="invoiceCustomerWise"
                checked={(form.invoiceCustomerWise ?? true) === option.value}
                onChange={() => set("invoiceCustomerWise", option.value)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{option.label}</strong>
                <span style={{ display: "block", fontSize: 12, color: "var(--color-muted, #667085)" }}>{option.help}</span>
              </span>
            </label>
          ))}
        </div>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600 }}>Location capture (mobile app)</h3>
          {LOCATION_CAPTURE.map((option) => (
            <label key={String(option.value)} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0" }}>
              <input
                type="radio"
                name="locationEveryMinute"
                checked={Boolean(form.locationEveryMinute) === option.value}
                onChange={() => set("locationEveryMinute", option.value)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{option.label}</strong>
                <span style={{ display: "block", fontSize: 12, color: "var(--color-muted, #667085)" }}>{option.help}</span>
              </span>
            </label>
          ))}
        </div>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600 }}>Create invoice (mobile app)</h3>
          {APP_INVOICE_USERS.map((option) => (
            <label key={String(option.value)} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0" }}>
              <input
                type="radio"
                name="appInvoiceForAllUsers"
                checked={Boolean(form.appInvoiceForAllUsers) === option.value}
                onChange={() => set("appInvoiceForAllUsers", option.value)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{option.label}</strong>
                <span style={{ display: "block", fontSize: 12, color: "var(--color-muted, #667085)" }}>{option.help}</span>
              </span>
            </label>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
          <div>
            <h3 style={{ fontWeight: 600 }}>Approval needed before a collection reduces the bill</h3>
            {APPROVAL_FLAGS.map(([field, label]) => (
              <Checkbox key={field} field={field} label={label} form={form} onChange={set} />
            ))}
          </div>
          <div>
            <h3 style={{ fontWeight: 600 }}>Workflow</h3>
            {WORKFLOW_FLAGS.map(([field, label]) => (
              <Checkbox key={field} field={field} label={label} form={form} onChange={set} />
            ))}
          </div>
        </div>
        <SheetPrintLayouts value={form.sheetPrintLayouts || {}} onChange={(layouts) => set("sheetPrintLayouts", layouts)} />
        {mobile ? (
          <div style={{ margin: "20px 0" }}>
            <h3 style={{ fontWeight: 600 }}>Mobile app</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
              <div>
                {MOBILE_OTP_FLAGS.map(([field, label]) => (
                  <Checkbox key={field} field={field} label={label} form={mobile} onChange={setMobileField} />
                ))}
              </div>
              <div>
                {MOBILE_FLAGS.map(([field, label]) => (
                  <Checkbox key={field} field={field} label={label} form={mobile} onChange={setMobileField} />
                ))}
              </div>
            </div>
            <div className="form-grid">
              {MOBILE_AMOUNTS.map(([field, label]) => (
                <div className="form-group" key={field}>
                  <label>{label}</label>
                  <input type="number" min="0" step="0.01" value={mobile[field] ?? ""} onChange={(e) => setMobileField(field, e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="form-grid">
          <div className="form-group">
            <label>Cheque bounce charges (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.chequeBounceCharges ?? ""}
              onChange={(e) => set("chequeBounceCharges", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Max discount (% of invoice, blank = no limit)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.maxDiscountPercent ?? ""}
              onChange={(e) => set("maxDiscountPercent", e.target.value)}
            />
          </div>
          {TEXT_FIELDS.map(([field, label]) => (
            <div className="form-group" key={field}>
              <label>{label}</label>
              <input value={form[field] || ""} onChange={(e) => set(field, e.target.value)} />
            </div>
          ))}
        </div>
        <div style={{ margin: "20px 0" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "0 24px" }}>
            {TENANT_CONFIG_FLAGS.map(([field, label]) => (
              <Checkbox key={field} field={field} label={label} form={form.tenantConfig || {}} onChange={setTenantConfig} />
            ))}
          </div>
          <div className="form-grid">
            {TENANT_CONFIG_NUMBERS.map(([field, label, step]) => (
              <div className="form-group" key={field}>
                <label>{label}</label>
                <input
                  type="number"
                  min="0"
                  step={step}
                  value={form.tenantConfig?.[field] ?? ""}
                  onChange={(e) => setTenantConfig(field, e.target.value)}
                />
              </div>
            ))}
            {TENANT_CONFIG_TEXTS.map(([field, label]) => (
              <div className="form-group" key={field}>
                <label>{label}</label>
                <input value={form.tenantConfig?.[field] || ""} onChange={(e) => setTenantConfig(field, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
        <div className="modal-actions">
          <button type="submit" className="primary" disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReceivableSettings;
